import { useRecoilValue } from "recoil";

import { EViewLocations, IClip } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { IsUserLoggedIn } from "MVVM/State";
import {
  UniqueIdentifierState,
  OverlayTriggerState,
  ViewLocationState,
  ViewSubLocationState,
} from "MVVM/State/iframe";

const isValidViewLocation = (loc: string): loc is EViewLocations =>
  Object.values(EViewLocations).includes(loc as EViewLocations);

const referrer = document.referrer || null;
const referring_domain = referrer ? new URL(referrer).hostname : null;
const initial_referrer = localStorage.getItem("initial_referrer") || null;
const initial_referring_domain = initial_referrer
  ? new URL(initial_referrer).hostname
  : null;
const knownUser =
  new URLSearchParams(window.location.search).get("known") === "true";

let analyticsClip: IClip | null = null;

const eventsLimitedToOnce = [
  "Component Viewed",
  "Embeddable Player Overlay Displayed",
];
const eventsTrackedOnce = new Set<string>();

export const useAnalyticsContext = ({
  clip,
}: {
  clip?: IClip | undefined;
} = {}) => {
  const isLoggedIn = useRecoilValue(IsUserLoggedIn);
  const uniqueIdentifier = useRecoilValue(UniqueIdentifierState);
  const overlayTrigger = useRecoilValue(OverlayTriggerState);
  const viewLocation = useRecoilValue(ViewLocationState);
  const viewSubLocation = useRecoilValue(ViewSubLocationState);

  if (clip && (!analyticsClip || analyticsClip.id !== clip.id)) {
    analyticsClip = clip;
  }

  const analyticsProperties = {
    clip_clipId: analyticsClip?.internalId,
    clip_clipType: "clip",
    clip_clipOwner: analyticsClip?.user?._id,
    clip_playerGameIdentifier: analyticsClip?.playerGameIdentifier,
    clip_game: analyticsClip?.game,
    clip_useCase: analyticsClip?.useCase,
    viewer_viewerIdentifier: uniqueIdentifier,
    viewer_viewerIsLoggedIn: isLoggedIn,
    viewer_viewerIsAllstar: knownUser,
    viewLocation_platform: analyticsClip?.developer?.slug,
    viewLocation_location: viewLocation
      ? isValidViewLocation(viewLocation)
        ? viewLocation
        : "invalid"
      : undefined,
    viewLocation_subLocation: viewSubLocation
      ? isValidViewLocation(viewLocation)
        ? viewSubLocation
        : `${viewLocation}:${viewSubLocation}`
      : isValidViewLocation(viewLocation)
      ? undefined
      : viewLocation || undefined,
    overlayTrigger,
    path: window.location.pathname,
    search: window.location.search,
    referrer,
    referring_domain,
    initial_referrer,
    initial_referring_domain,
  };

  const track = (
    eventName: string,
    extraProperties: Record<string, unknown> = {},
  ) => {
    if (
      eventsLimitedToOnce.includes(eventName) &&
      !extraProperties.promotionName
    ) {
      const locationIdentifier = extraProperties.locationIdentifier as string;
      if (locationIdentifier) {
        const componentKey = `${locationIdentifier}_${
          extraProperties.location || "unknown"
        }`;

        if (eventsTrackedOnce.has(componentKey)) {
          return;
        }
        eventsTrackedOnce.add(componentKey);
      }
    }

    analytics.track(eventName, {
      ...analyticsProperties,
      ...extraProperties,
    });
  };

  return { track, analyticsProperties };
};
